// Flexfoil prior client — the cheap-prior tier of the High-Lift multifidelity workbench (FXC-9009).
//
// The workbench mixes three data sources over the design+operation grid:
//   1. flexfoil priors  (inviscid + viscous, this module)  — cheap, ~ms
//   2. 2D RANS          (Flow360, the ADB explorer grid)   — expensive, ground truth
//   3. Tailor           (adbTailorSlice, in-browser)       — fits the RANS−prior residual
//
// This calls thread-api `POST /projects/{id}/highlift/priors`, which evaluates the pip
// `flexfoil` engine (Airfoil.with_flap(...).polar(...)) at the exact grid points we pass it,
// so the host can compute residual = RANS_CL − prior_CL per point and fit Tailor on it.
// Server: thread/thread-api/app/routes/highlift.py + app/services/flexfoil_prior.py.

import { apiClient } from "./client";

export type PriorFidelity = "inviscid" | "viscous";

/** One grid point to evaluate a prior at. Mirrors the RANS run's value_json axes. */
export interface PriorPoint {
  alpha_deg: number;
  mach: number;
  reynolds: number;
  /** Plain-flap deflection for this point (maps to the deployed flap angle). */
  flap_deploy_deg?: number | null;
}

/** Plain-flap hinge geometry used to build the prior airfoil. */
export interface FlapSpec {
  hinge_x: number;
  hinge_y_frac: number;
  /** Fixed deflection for every point; omit to use each point's flap_deploy_deg. */
  deflection_deg?: number | null;
}

export interface PriorRequest {
  points: PriorPoint[];
  flap?: FlapSpec;
  fidelities?: PriorFidelity[];
  /** Optional Selig/Lednicer .dat override; default is the LS(1)-0417 baseline. */
  airfoil_dat?: string | null;
  ncrit?: number;
  max_iter?: number;
}

export interface PriorFidelityResult {
  CL: number | null;
  CD: number | null;
  CM: number | null;
  converged: boolean;
}

export interface PriorPointResult {
  alpha_deg: number;
  mach: number;
  reynolds: number;
  flap_deploy_deg: number;
  inviscid?: PriorFidelityResult | null;
  viscous?: PriorFidelityResult | null;
}

export interface PriorResponse {
  /** False when flexfoil is not installed on the API host — frontend degrades gracefully. */
  available: boolean;
  flexfoil_version: string | null;
  airfoil: string;
  note: string | null;
  points: PriorPointResult[];
  /** True when THIS host can re-mesh + solve each sweep point locally. When false the
   *  only RANS path is the cloud fork on the SINGLE staged mesh, so a multi-deploy sweep
   *  is structurally all-failed — the Sweep panel locks the deploy/flaperon axes to "hold"
   *  in that case (FXC-9009 Phase 2). Optional for forward-compat with older API hosts. */
  local_solve_available?: boolean;
}

export async function fetchHighliftPriors(projectId: string, req: PriorRequest): Promise<PriorResponse> {
  return apiClient<PriorResponse>(`/projects/${projectId}/highlift/priors`, {
    method: "POST",
    body: req,
  });
}

// --- cloud RANS solve (fork a Flow360 case per recommended point) -----------

export interface SolvePoint {
  [axis: string]: number | undefined;
  alpha_deg: number;
  mach: number;
  reynolds?: number;
  flap_deploy_deg?: number;
  temperature?: number;
}

/** An actuator-disk blowing source on the solve wire (the RANS mesher's momentum-source contract). */
export interface SolveDisk {
  center: [number, number];
  radius: number;
  thickness: number;
  axis: [number, number];
  loading: number;
}

export interface LocalSolveInput {
  elements?: { name: string; contour: [number, number][] }[];
  /** ALL actuator-disk blowing sources for this point (FXC-9009 N-disk wire). The server falls
   *  back to wrapping the legacy single `disk` when only that is present. */
  disks?: SolveDisk[];
  /** @deprecated Back-compat single-disk wire (== `disks[0]`). New callers send `disks`. */
  disk?: SolveDisk | null;
  temperature?: number;
}

/** NN-flowfield warm-start request (FXC-9009). When set, the solver seeds each case from the
 *  flowfield-NN prediction instead of freestream — proven ~2.8× fewer steps to the SAME converged
 *  CL on the blown-attached regime (scripts/warmstart/*). The (env-gated) local backend evaluates
 *  the NN at its mesh nodes and writes the conservative-variable restart (the proven nondim map);
 *  the cloud fork ignores unknown fields, so this is safe to always send. */
export interface WarmStartSpec {
  mode: "nn-flowfield";
  /** URL the backend fetches the exported MLP weights from (the same artifact the client previews). */
  weights_url: string;
  /** Blowing coefficient the NN field is evaluated at (matches the solve point's disk loading). */
  blow?: number;
}

export interface SolveRequest {
  points: SolvePoint[];
  max_steps?: number;
  /** Seed each solve from the flowfield-NN instead of freestream (FXC-9009; ~2.8× fewer steps). */
  warm_start?: WarmStartSpec | null;
  design?: Record<string, unknown>;
  operation?: Record<string, unknown>;
  /** Live foil element contours, so the (env-gated) local backend can re-mesh the
   *  actual design rather than the staged blown-lift mesh. The cloud backend
   *  ignores unknown fields. */
  elements?: { name: string; contour: [number, number][] }[];
  local_inputs?: LocalSolveInput[];
  /** Client-generated hex token naming the local-solve run dir, so the UI can poll
   *  `fetchHighliftSolveProgress` for live convergence. Ignored by the cloud fork. */
  progress_token?: string;
  /** FXC-9009 buffer-lineage: when the solved buffer is a COMMITTED named variant, its saved-geometry
   *  slug, so the run's provenance geometry links back to the named variant (and its parent chain).
   *  Omit for a scratch/uncommitted buffer. */
  geometry_slug?: string | null;
}

export interface SolveSubmission {
  alpha_deg: number;
  mach: number;
  reynolds: number;
  flap_deploy_deg: number;
  case_id: string | null;
  run_path: string | null;
  error: string | null;
  /** Coefficients the local re-mesh backend returns inline (cloud forks leave null). */
  CL?: number;
  CD?: number;
  L_over_D?: number;
  /** Which backend serviced this submission (e.g. "local" | "cloud"). */
  backend?: string;
}

export interface SolveResponse {
  submitted: SolveSubmission[];
  failed: number;
  cached: number;
  flow360_project: string;
  note: string | null;
}

/** Fork a cloud RANS case per point on the staged blown-lift mesh. Results land
 *  asynchronously (case_poller ingests CL/CD into the run nodes, ~1 min each). */
export async function solveHighliftRuns(
  projectId: string,
  req: SolveRequest,
  branch = "main",
): Promise<SolveResponse> {
  const params = new URLSearchParams({ branch });
  return apiClient<SolveResponse>(`/projects/${projectId}/highlift/solve?${params.toString()}`, {
    method: "POST",
    body: req,
  });
}

// --- WebGPU client-solve persist (FXC-9009 P6) -------------------------------
//
// A WebGPU solve is computed IN THE BROWSER (no SSH/cloud). To make its point join
// the ADB grid / Tailor / lineage like a RANS run, POST the already-computed
// coefficients here: the server writes a `run` node (metadata.solver='webgpu') reusing
// the SAME rans_provenance flat axes + run_path scheme as the RANS /solve path, so the
// node dedupes/joins the grid identically. This is a SEPARATE, additive endpoint — it
// does NOT touch the cache->local->cloud resolution in /solve. Persist ONLY explicit
// (cold) solves; never drag-resolve previews.

/** A client-computed WebGPU solve to persist as a completed run node. `design`/`operation`
 *  are the SAME records `SolveRequest` takes (so the flat axes — and thus the run_path /
 *  mesh dedupe — match the RANS path). The coefficients are the in-browser solve's outputs. */
export interface WebGpuRunRequest {
  alpha_deg: number;
  mach: number;
  reynolds?: number;
  CL: number;
  CD?: number | null;
  CM?: number | null;
  L_over_D?: number | null;
  design?: Record<string, unknown>;
  operation?: Record<string, unknown>;
  /** Buffer-lineage: the COMMITTED variant slug (when the buffer matches its saved baseline),
   *  so the run's provenance geometry links back to the named variant. Omit for a scratch buffer. */
  geometry_slug?: string | null;
  /** Solver provenance tag for the persisted run node. Default "webgpu" (in-browser);
   *  the local-V100 RANS backend sends "rans-v100". Server sanitizes to those two. */
  solver?: string;
  /** The solved center-span FlowMesh. When present the server gzips + blob-stores it and
   *  stamps the run node with a pointer, so the field survives reload (a WebGPU/V100 run
   *  has no Flow360 case / box out-dir for GET /flow to resolve otherwise). */
  flow_mesh?: FlowMesh | null;
}

export interface WebGpuRunResponse {
  run_path: string;
  solver: string;
  CL: number;
  CD: number | null;
  L_over_D: number | null;
}

/** Persist a client-computed WebGPU solve as a completed run node so its point joins the
 *  ADB grid / Tailor / lineage like a RANS run. Reuses the rans_provenance run_path scheme
 *  (metadata.solver='webgpu', no Flow360 case). Call ONLY after an explicit (cold) solve. */
export async function persistWebGpuRun(
  projectId: string,
  req: WebGpuRunRequest,
  branch = "main",
): Promise<WebGpuRunResponse> {
  const params = new URLSearchParams({ branch });
  return apiClient<WebGpuRunResponse>(`/projects/${projectId}/highlift/webgpu-run?${params.toString()}`, {
    method: "POST",
    body: req,
  });
}

// --- saved geometries (AC-5: name a design, reload it later) ----------------
//
// The human-reloadable artifact: the FULL HighLiftAirfoil config persisted under
// a `geometry` kind node at `geometries.blownlift.user.<slug>`. Distinct from the
// design_hash provenance/dedupe node the solve path writes — see the route doc in
// thread/thread-api/app/routes/highlift.py.

import type { HighLiftAirfoil } from "../features/highlift/geometry";

/** A named high-lift geometry stored in the project. `config` is the full
 *  HighLiftAirfoil the workbench seeds `useFoilDesign`/`loadConfig` from. */
export interface SavedGeometry {
  node_id: string;
  name: string;
  slug: string;
  path: string;
  config: HighLiftAirfoil;
  updated_at?: string | null;
  /** Provenance design hash of the stored config (the same flatten_design/design_hash the /solve
   *  path keys on) — the variant's stable geometry identity. Present on save + list. */
  design_hash?: string | null;
  /** XFOIL buffer-lineage: the baseline slug this variant was reshaped from, when a `derived_from`
   *  lineage edge was written. Null for a root save. Surfaces the family tree in the Library. */
  derived_from_slug?: string | null;
  derived_from_node_id?: string | null;
}

interface SaveGeometryResponse {
  geometry: SavedGeometry;
}
interface ListGeometriesResponse {
  geometries: SavedGeometry[];
}

/** Save a named high-lift geometry into the project. Re-saving the same name
 *  (after slugifying) overwrites it server-side.
 *
 *  XFOIL buffer-foil → Thread lineage (FXC-9009): pass `derived_from` = the BASELINE's slug (or node
 *  id) the buffer was reshaped from, and the server writes a `derived_from` lineage edge baseline →
 *  this variant, so the variant appears in the Lineage view as a child of its parent. */
export async function saveHighliftGeometry(
  projectId: string,
  branch: string,
  body: { name: string; config: HighLiftAirfoil; derived_from?: string | null },
): Promise<SavedGeometry> {
  const params = new URLSearchParams({ branch });
  const res = await apiClient<SaveGeometryResponse>(
    `/projects/${projectId}/highlift/geometries?${params.toString()}`,
    { method: "POST", body },
  );
  return res.geometry;
}

/** List the project's saved high-lift geometries (name + node id + stored config). */
export async function listHighliftGeometries(
  projectId: string,
  branch = "main",
): Promise<SavedGeometry[]> {
  const params = new URLSearchParams({ branch });
  const res = await apiClient<ListGeometriesResponse>(
    `/projects/${projectId}/highlift/geometries?${params.toString()}`,
  );
  return res.geometries;
}

// --- flow-field slice (fetch-on-select LIC field) ---------------------------

// The wire shape of `GET /projects/{id}/highlift/flow` is exactly the FlowMesh
// the three.js FlowVisLayer already declares + consumes, so we re-export that
// type rather than redeclare it — one source of truth for the slice contract.
export type { FlowMesh, SurfaceCpElement, ElementForce } from "../features/highlift/foil/FlowVisLayer";
import type { FlowMesh } from "../features/highlift/foil/FlowVisLayer";

/** Fetch a solved run's center-span 2D RANS slice as a `FlowMesh`.
 *
 *  Returns `null` (not a throw) when the slice is absent — older cases predate
 *  the Slice output, and the run/case/credential may legitimately be missing,
 *  all of which the server reports as 404. The caller renders FlowVisLayer's
 *  empty state in that case. Other errors propagate. */
export async function fetchHighliftFlowMesh(
  projectId: string,
  runId: string,
  branch = "main",
  step?: number | null,
): Promise<FlowMesh | null> {
  const params = new URLSearchParams({ run: runId, branch });
  if (step != null) params.set("step", String(step));
  try {
    return await apiClient<FlowMesh>(`/projects/${projectId}/highlift/flow?${params.toString()}`);
  } catch (error: unknown) {
    // apiClient throws `Error("API <status>: …")`; a 404 is the expected-empty
    // signal (no slice for this run), so degrade to null instead of throwing.
    if (error instanceof Error && error.message.startsWith("API 404:")) return null;
    throw error;
  }
}

// --- live solve progress (local backend) ------------------------------------

/** Live convergence snapshot for a local solve, keyed by its `progress_token`. */
export interface SolveProgress {
  status: "meshing" | "preprocessing" | "solving" | "running" | "done";
  stage?: string | null;
  /** 0–100 within the solver stage (null while meshing/preprocessing). */
  percentage?: number | null;
  /** Latest pseudo-step the solver has written. */
  step?: number | null;
  /** Latest lift coefficient — the marker that settles as the flow converges. */
  CL?: number | null;
  CD?: number | null;
  /** Downsampled CL convergence trajectory, for the client to replay through the marker. */
  cl_history?: number[] | null;
}

/** Poll a running local solve's convergence. Returns `null` when progress isn't
 *  available (local solve disabled → 404, or an invalid token → 400) so the caller
 *  can quietly fall back to an indeterminate spinner. */
export async function fetchHighliftSolveProgress(
  projectId: string,
  token: string,
  branch = "main",
): Promise<SolveProgress | null> {
  const params = new URLSearchParams({ token, branch });
  try {
    return await apiClient<SolveProgress>(`/projects/${projectId}/highlift/solve/progress?${params.toString()}`);
  } catch (error: unknown) {
    if (error instanceof Error && /^API (404|400):/.test(error.message)) return null;
    throw error;
  }
}
